import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Product } from '../models/product.model';
import { Address } from '../models/address.model';
import { ToastrService } from 'ngx-toastr';

interface EudrCheckResult {
    hasRestriction: boolean;
    footerMessage: string | null;
    errorMessage: string | null;
}

@Injectable()
export class EUDRService {
    private readonly footerMessageSubject = new BehaviorSubject<string | null>(null);
    private readonly showWarningSubject = new BehaviorSubject<boolean>(false);
    private readonly restrictionStatusSubject = new BehaviorSubject<boolean>(false);
    private readonly errorMessageSubject = new BehaviorSubject<string | null>(null);

    constructor(private toastr: ToastrService) {}

    checkEudrRestrictions(product: Product, address: Address | null): boolean {
        const result = this.evaluateEudrRestriction(product, address);
        this.applyEudrState(result);
        return result.hasRestriction;
    }

    private evaluateEudrRestriction(product: Product, address: Address | null): EudrCheckResult {
        if (!product || !address || !address.eudr_applicable) {
            return { hasRestriction: false, footerMessage: null, errorMessage: null };
        }

        const checks = product.eudr_checks;
        const canAddToCart = checks?.canAddToCart ?? true;
        const hasRestriction = !canAddToCart && address.eudr_applicable;

        return {
            hasRestriction,
            footerMessage: hasRestriction ? checks?.footerMessage ?? null : null,
            errorMessage: hasRestriction ? checks?.errorMessage ?? null : null,
        };
    }

    private applyEudrState(result: EudrCheckResult): void {
        const { hasRestriction, footerMessage, errorMessage } = result;

        this.footerMessageSubject.next(footerMessage);
        this.showWarningSubject.next(!!footerMessage);
        this.restrictionStatusSubject.next(hasRestriction);
        this.errorMessageSubject.next(errorMessage);
    }

    updateEudrStateForAddress(
        product: Product,
        address: Address | null,
        setAddressResult: { success: boolean }
    ): boolean {
        if (!setAddressResult.success || !address || !product) {
            this.clearEudrState();
            return false;
        }
        return this.checkEudrRestrictions(product, address);
    }

    checkEudrForAddress(product: Product, address: Address | null): boolean {
        return this.checkEudrRestrictions(product, address);
    }

    clearEudrState(): void {
        this.footerMessageSubject.next(null);
        this.showWarningSubject.next(false);
        this.restrictionStatusSubject.next(false);
        this.errorMessageSubject.next(null);
    }

    // Observables
    getFooterMessage(): Observable<string | null> {
        return this.footerMessageSubject.asObservable();
    }

    getShowWarning(): Observable<boolean> {
        return this.showWarningSubject.asObservable();
    }

    getRestrictionStatus(): Observable<boolean> {
        return this.restrictionStatusSubject.asObservable();
    }

    getErrorMessage(): Observable<string | null> {
        return this.errorMessageSubject.asObservable();
    }

    // Sync Getters
    getCurrentShowWarning(): boolean {
        return this.showWarningSubject.getValue();
    }

    getCurrentRestrictionStatus(): boolean {
        return this.restrictionStatusSubject.getValue();
    }

    getCurrentErrorMessage(): string | null {
        return this.errorMessageSubject.getValue();
    }

    // Manual override
    setFooterMessage(message: string | null): void {
        this.footerMessageSubject.next(message);
        this.showWarningSubject.next(!!message);
        this.restrictionStatusSubject.next(!!message);
        this.errorMessageSubject.next(null);
    }
}
